import { useEffect, useState } from "react";
import { PaperPlaneTilt, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inr, fmtDateTime, clientReqBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const TYPES = ["Additional AUM Request", "Interest Withdrawal Request", "Emergency Capital Withdrawal Request", "Others"];

export default function InvestorRequests() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ request_type: "", subject: "", description: "", requested_amount: "" });

  const load = () => api.get("/investor/requests").then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.request_type) { toast.error("Select a request type."); return; }
    if (!form.subject.trim() || !form.description.trim()) { toast.error("Subject and description are required."); return; }
    setBusy(true);
    try {
      const payload = { request_type: form.request_type, subject: form.subject.trim(), description: form.description.trim() };
      if (form.requested_amount !== "") payload.requested_amount = Number(form.requested_amount);
      const r = await api.post("/investor/requests", payload);
      toast.success(`Request ${r.data.request_code} submitted to your Wealth Relationship Manager.`);
      setForm({ request_type: "", subject: "", description: "", requested_amount: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <AppLayout title="Requests"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!rows) return <AppLayout title="Requests"><PageLoader /></AppLayout>;

  return (
    <AppLayout title="Requests" subtitle="Raise a request to your assigned Wealth Relationship Manager and track its status.">
      <div className="fade-up space-y-6 max-w-5xl">
        <div className="glass-card p-7" data-testid="request-form-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 flex items-center justify-center">
              <Ticket size={20} weight="duotone" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white">Raise a Request</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">Automatically routed to your assigned Wealth Relationship Manager.</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="label-caps">Request Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="req-type-options">
                {TYPES.map((t) => (
                  <button key={t} type="button" data-testid={`req-type-${t.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    onClick={() => setForm({ ...form, request_type: t })}
                    className={`text-left text-xs font-semibold rounded-lg border px-3.5 py-3 transition-colors duration-200 ${
                      form.request_type === t ? "pill-active" : "border-white/10 text-zinc-400 hover:text-zinc-200 hover:border-white/20"
                    }`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {form.request_type && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="label-caps">Subject</label>
                    <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      data-testid="req-subject" className="glass-input" placeholder="Brief summary" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="label-caps">Requested Amount (₹){form.request_type === "Others" ? " — optional" : ""}</label>
                    <input type="number" value={form.requested_amount}
                      onChange={(e) => setForm({ ...form, requested_amount: e.target.value })}
                      data-testid="req-amount" className="glass-input" placeholder="e.g. 500000" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="label-caps">Description</label>
                  <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="req-description" className="glass-input h-auto py-2.5 resize-none" placeholder="Describe your request..." />
                </div>
                <Button onClick={submit} disabled={busy} data-testid="req-submit"
                  className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold emerald-glow">
                  <PaperPlaneTilt size={15} className="mr-1.5" weight="bold" /> {busy ? "Submitting..." : "Submit Request"}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="glass-card overflow-hidden" data-testid="my-requests-card">
          <div className="px-6 pt-5 pb-4">
            <h3 className="font-heading font-semibold text-white">My Requests</h3>
          </div>
          {rows.length === 0 ? <EmptyState message="No client requests yet." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="my-requests-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>Request ID</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Subject</th>
                    <th className={`${thCls} text-right`}>Amount</th>
                    <th className={thCls}>Status</th>
                    <th className={thCls}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} onClick={() => setSel(r)} data-testid={`my-request-row-${r.id}`}
                      className={`${rowCls} cursor-pointer`}>
                      <td className={`${tdCls} tnum text-xs text-emerald-300`}>{r.request_code}</td>
                      <td className={`${tdCls} text-xs text-zinc-300`}>{r.type}</td>
                      <td className={`${tdCls} text-xs text-zinc-300 max-w-[220px] truncate`}>{r.subject}</td>
                      <td className={`${tdCls} text-right tnum text-zinc-100`}>{r.requested_amount != null ? inr(r.requested_amount) : "—"}</td>
                      <td className={tdCls}><Badge variant="outline" className={`text-[10px] ${clientReqBadge(r.status)}`} data-testid={`my-req-status-${r.id}`}>{r.status}</Badge></td>
                      <td className={`${tdCls} tnum text-[11px] text-zinc-500 whitespace-nowrap`}>{fmtDateTime(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl" data-testid="my-request-detail">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-white flex items-center gap-3 flex-wrap">
                  {sel.request_code}
                  <Badge variant="outline" className={`text-[10px] ${clientReqBadge(sel.status)}`}>{sel.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-white font-medium" data-testid="my-req-subject">{sel.subject}</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {sel.type}{sel.requested_amount != null ? ` · ${inr(sel.requested_amount)}` : ""} · {fmtDateTime(sel.created_at)}
                  </p>
                  <p className="text-sm text-zinc-300 mt-3 whitespace-pre-wrap">{sel.description}</p>
                </div>
                <div>
                  <p className="label-caps mb-2">WRM Responses</p>
                  <div className="space-y-2.5" data-testid="my-req-thread">
                    {(sel.thread || []).length === 0 && <p className="text-xs text-zinc-600">No responses yet.</p>}
                    {(sel.thread || []).map((t) => (
                      <div key={t.id} className="border border-white/[0.06] bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-emerald-300">{t.by}</p>
                          <p className="text-[10px] text-zinc-600 tnum">{fmtDateTime(t.at)}</p>
                        </div>
                        <p className="text-sm text-zinc-300 mt-1.5">{t.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
